/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import parse from 'html-react-parser';
import PropTypes from 'prop-types';

import courseLogo from '../../assets/icons/logo_curso.png';
import iconQuizz from '../../assets/icons/quizzIcon.png';
import page from '../../assets/icons/text.png';
import { AddRollerCourse, GetTopicById } from '../../services/courses';
import { setAnalytics } from '../../utils';

const useStyles = makeStyles((theme) => ({
  '@global': {
    '*::-webkit-scrollbar': {
      backgroundColor: theme.palette.background.primary,
      borderRadius: '10px',
    },
    '*::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.background.secondary,
      borderRadius: '10px',
    },
  },
  paper: {
    position: 'absolute',
    [theme.breakpoints.between('lg', 'xl')]: {
      top: '0%',
      button: '10%',
      left: '20vh',
      maxWidth: 1040,
      minHeight: '568px',
      backgroundColor: theme.palette.background.card,
      borderRadius: '16px',
      boxShadow: theme.shadows[5],
      padding: theme.spacing(1, 3, 1),
    },
    [theme.breakpoints.up('xl')]: {
      top: '5%',
      button: '10%',
      left: '35vh',
      maxWidth: 1040,
      minHeight: '568px',
      backgroundColor: theme.palette.background.card,
      borderRadius: '16px',
      boxShadow: theme.shadows[5],
      padding: theme.spacing(1, 3, 1),
    },
  },
  iconClose: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'end',
  },
  titleText: {
    color: theme.palette.common.white,
    fontFamily: 'Raleway',
    fontSize: '22px',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  description: {
    color: theme.palette.common.white,
    fontSize: '14px',
    fontFamily: 'Raleway',
  },
  mentorCard: {
    background: '#1E3E63',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    borderRadius: '8px',
  },
  mentorTitle: {
    color: theme.palette.background.info,
    fontSize: '16px',
    fontWeight: 700,
    fontFamily: 'Raleway',
    margin: theme.spacing(0, 0, 1, 0),
    textTransform: 'uppercase',
  },
  mentorName: {
    color: theme.palette.text.default,
    fontSize: '24px',
    fontWeight: 700,
    fontFamily: 'Raleway',
  },
  mentorSubname: {
    color: theme.palette.text.default,
    fontSize: '16px',
    fontWeight: 500,
    fontFamily: 'Raleway',
  },
  imgCourse: {
    width: '50px',
    height: '50px',
    marginRight: theme.spacing(2),
  },
  cardSummary: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color: theme.palette.common.white,
    background: '#1E3E63',
    minWidth: '460px',
    margin: theme.spacing(1, 2, 0, 0),
    padding: theme.spacing(1, 3, 1, 1),
  },
  titleSummary: {
    fontFamily: 'Raleway',
    fontSize: '16px',
    fontWeight: 700,
    textAlign: 'left',
    color: theme.palette.common.white,
    margin: theme.spacing(0, 0, 2, 0),
  },
  card: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'start',
  },
  coursesSection: {
    overflowX: 'hidden',
    background: 'transparent',
    height: '470px',
  },
  iconTheme: {
    width: '25px',
    height: '20px',
    marginRight: theme.spacing(1),
  },
  titleTheme: {
    flexGrow: 1,
    fontFamily: 'Raleway',
    fontSize: '13px',
    color: theme.palette.common.white,
  },
  hr: {
    maxWidth: '490px',
    maxHeight: '0.2px',
    background: theme.palette.background.default,
    margin: theme.spacing(2, 0),
  },
  mentorHr: {
    maxWidth: '350px',
    maxHeight: '1px',
    border: '1px solid',
    color: theme.palette.background.info,
    background: theme.palette.background.info,
    marginRight: theme.spacing(1),
  },
  divider: {
    width: '1px',
    height: '550px',
    border: 'none',
    background: theme.palette.background.default,
    margin: theme.spacing(0, 5),
  },
  imgMentor: {
    borderRadius: '50%',
    width: 85,
    height: 85,
    padding: theme.spacing(0, 0, 0, 1),
  },
  mentorSection: {
    margin: theme.spacing(2, 0, 0, 0),
  },
  button: {
    padding: theme.spacing(1, 5),
    borderRadius: '10px',
    fontFamily: 'Raleway',
  },
  buttonAction: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
  },
  skeletonTopic: {
    margin: theme.spacing(1, 0),
  },
  skeletonTopicContainer: {
    margin: theme.spacing(0, 0, 4, 0),
  },
}));

function LessonByTopic({ lessons }) {
  const classes = useStyles();
  return (
    <>
      {lessons.map((lesson) => (
        <Box className={classes.cardSummary} key={lesson.id}>
          <img src={page} alt="page" className={classes.iconTheme} />
          <Typography className={classes.titleTheme}>{lesson.name}</Typography>
          <Typography className={classes.description}>
            {lesson.duration}
            {' '}
            min
          </Typography>
        </Box>
      ))}
    </>
  );
}

LessonByTopic.propTypes = {
  lessons: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function SkeletonTopics() {
  const classes = useStyles();
  return (
    <Box className={classes.skeletonTopicContainer}>
      <Skeleton variant="rect" width={500} height={10} className={classes.skeletonTopic} />
      <Skeleton variant="rect" width={500} height={30} className={classes.skeletonTopic} />
      <Skeleton variant="rect" width={500} height={30} className={classes.skeletonTopic} />
      <Skeleton variant="rect" width={500} height={30} className={classes.skeletonTopic} />
      <Skeleton variant="rect" width={500} height={30} className={classes.skeletonTopic} />
      <Skeleton variant="rect" width={500} height={30} className={classes.skeletonTopic} />
    </Box>
  );
}
function Topics({ topic }) {
  const classes = useStyles();
  const [printTopic, setPrintTopic] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const getTopicData = async () => {
    const dataTopic = await GetTopicById(topic.id);
    setPrintTopic(dataTopic.data);
    setIsLoading(false);
  };

  useEffect(() => {
    getTopicData();
  }, []);

  return (
    <Box>
      {isLoading ? <SkeletonTopics />
        : (
          <Grid key={printTopic.id}>
            <Box>
              <Typography className={classes.titleSummary}>
                {printTopic.name}
              </Typography>
              <Box>
                <LessonByTopic lessons={printTopic.lessons} />
              </Box>
              {printTopic.quiz.length !== 0 ? (
                <Box className={classes.cardSummary}>
                  <img src={iconQuizz} alt="icon" className={classes.iconTheme} />
                  <Typography className={classes.titleTheme}>Evaluación</Typography>
                </Box>
              ) : null}
              {printTopic.practicalCase.length !== 0 ? (
                <Box className={classes.cardSummary}>
                  <img src={iconQuizz} alt="icon" className={classes.iconTheme} />
                  <Typography className={classes.titleTheme}>Caso Practico</Typography>
                </Box>
              ) : null }
            </Box>
            <hr className={classes.hr} />
          </Grid>
        ) }
    </Box>
  );
}
Topics.propTypes = {
  topic: PropTypes.objectOf(PropTypes.string).isRequired,
};

function ThemeAndLessons({ themesModal }) {
  return (
    <Box>
      { themesModal.map((e) => (
        <Topics topic={e} />
      ))}
    </Box>
  );
}

ThemeAndLessons.propTypes = {
  themesModal: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function ModalCourse({
  id, titleModal, descriptionModal, themesModal, close,
}) {
  const classes = useStyles();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const { sub } = useSelector((store) => store.login);

  const enrollCourse = async () => {
    setIsLoading(true);
    await AddRollerCourse(sub, id);
    setIsLoading(false);
    sessionStorage.setItem('noneInit', false);
    setAnalytics('Course', 'Btn_EmpezarTalentInnovation');
    navigate(`/map/${id}`);
  };
  return (
    <div className={classes.paper}>
      <CardActions className={classes.buttonAction}>
        <Box>{close}</Box>
      </CardActions>
      <Box className={classes.card}>
        <img className={classes.imgCourse} src={courseLogo} alt="courseLogo" />
        <Box className={classes.infoSection}>
          <Typography className={classes.titleText}>{titleModal}</Typography>
          <Box>
            <Typography className={classes.description}>
              {parse(descriptionModal)}
            </Typography>
          </Box>
          <Box className={classes.mentorSection}>
            <Typography className={classes.mentorTitle}>A cargo de</Typography>
            <Card className={classes.mentorCard}>
              <img
                src="https://talent-innovation.s3.us-east-2.amazonaws.com/mentors-assets/andres-mendez.jpg"
                className={classes.imgMentor}
                alt="imgmentor"
              />
              <CardContent>
                <Typography className={classes.mentorName}>
                  Andrés Mendez
                </Typography>
                <Typography className={classes.mentorSubname}>
                  Gerente de transformación digital y experiencia de clientes
                </Typography>
                <hr className={classes.mentorHr} />
                <Typography className={classes.description}>
                  Experto en Design Thinking
                </Typography>
                <Typography className={classes.description}>
                  Forma parte de La Kolmena
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
        <hr className={classes.divider} />
        <Box>
          <Typography className={classes.mentorTitle}>Temario</Typography>
          <Box className={classes.coursesSection}>
            <Box>
              <Typography className={classes.titleSummary}>
                <ThemeAndLessons themesModal={themesModal} />
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <CardActions className={classes.buttonAction}>
        {isLoading ? (
          <Button
            variant="contained"
            color="secondary"
            className={classes.button}
          >
            Cargando...
          </Button>
        ) : (
          <Button
            variant="contained"
            color="secondary"
            onClick={enrollCourse}
            className={classes.button}
          >
            Empezar
          </Button>
        )}
      </CardActions>
    </div>
  );
}

ModalCourse.propTypes = {
  id: PropTypes.string.isRequired,
  titleModal: PropTypes.string.isRequired,
  descriptionModal: PropTypes.string.isRequired,
  themesModal: PropTypes.arrayOf(PropTypes.string).isRequired,
  close: PropTypes.string.isRequired,
};

export default ModalCourse;
